/**
 * PerformanceConfigStep — Step 2 of the Rodeo Wizard
 *
 * Manages performance (go/slack) time slots for the rodeo.
 * Each performance has a name, date, times, sort order, and slack flag.
 *
 * Add/edit is driven by the shared PerformanceDialog modal — the same
 * component used by the My Rodeos expanded list for out-of-wizard edits.
 *
 * Inside the Edit Performance dialog, the events list is rendered as a
 * toggle list (`EventTogglesSlot`) over ALL rodeo events, with a master
 * toggle at the top. Events are CREATED in Step 2 (EventConfigStep);
 * Step 3's responsibility is purely assigning events to performances.
 */

import { useState } from 'react';
import {
  Plus,
  Trash2,
  GripVertical,
  Clock,
  Sun,
  Pencil,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { useRodeoStore } from '@/state_management_and_stores/rodeo.store';
import { formatDate, formatTime } from '@/utils/formatters';
import { getTimezoneAbbreviation } from '@/utils/timezone';
import {
  PerformanceDialog,
  type PerformanceDialogValues,
} from '@/components/rodeo-wizard/PerformanceDialog';
import { EventTogglesSlot } from '@/components/rodeo-wizard/EventTogglesSlot';

export function PerformanceConfigStep() {
  const performances = useRodeoStore((s) => s.performances);
  const addPerformance = useRodeoStore((s) => s.addPerformance);
  const updatePerformance = useRodeoStore((s) => s.updatePerformance);
  const removePerformance = useRodeoStore((s) => s.removePerformance);

  const events = useRodeoStore((s) => s.events);
  // Pull the rodeo's saved timezone abbreviation so the perf-time picker
  // can label the input with which zone the wall-clock value is anchored to.
  const timezone = useRodeoStore((s) => s.basicInfo.timezone);
  const tzAbbr = getTimezoneAbbreviation(timezone);

  // editingKey === null && open → Add mode
  // editingKey === '<_key>' && open → Edit mode targeting that row
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const editingPerformance = editingKey
    ? performances.find((p) => p._key === editingKey)
    : undefined;

  const handleAddClick = () => {
    setEditingKey(null);
    setDialogOpen(true);
  };

  const handleEditClick = (key: string) => {
    setEditingKey(key);
    setDialogOpen(true);
  };

  const handleSubmit = (values: PerformanceDialogValues) => {
    if (editingKey) {
      updatePerformance(editingKey, values);
    } else {
      addPerformance({
        ...values,
        sort_order: performances.length + 1,
      });
    }
    setDialogOpen(false);
    setEditingKey(null);
  };

  // "Add & Next Day" flow — commit the perf to the draft store but leave the
  // dialog open. The dialog itself advances the date; everything else stays
  // so the user can chain multi-day perfs with minimal retyping.
  const handleSubmitAndNextDay = (values: PerformanceDialogValues) => {
    addPerformance({
      ...values,
      sort_order: performances.length + 1,
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Performances</h2>
          <p className="text-sm text-muted-foreground">
            Define performance/slack time slots for contestants.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={handleAddClick}>
          <Plus className="h-4 w-4 mr-1" /> Add Performance
        </Button>
      </div>

      {/* Performance list */}
      {performances.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="No performances yet"
          description="Add at least one performance or slack slot for your rodeo."
        />
      ) : (
        <div className="space-y-2">
          {[...performances]
            .sort((a, b) => {
              // Order by start datetime (performance_date + start_time). Rows
              // without a date sink to the bottom; ties fall back to sort_order
              // so a reorder still wins when dates match.
              const aKey = `${a.performance_date || '9999-12-31'} ${a.start_time || '23:59:59'}`;
              const bKey = `${b.performance_date || '9999-12-31'} ${b.start_time || '23:59:59'}`;
              if (aKey !== bKey) return aKey < bKey ? -1 : 1;
              return a.sort_order - b.sort_order;
            })
            .map((perf) => {
              const pending = !!perf._pendingDelete;
              const pendingEdit = !pending && !!perf._pendingEdit;
              return (
                <div
                  key={perf._key}
                  className={`flex items-center gap-3 border rounded-md px-3 py-2.5 ${
                    pending
                      ? 'opacity-60 border-destructive/40 bg-destructive/5'
                      : pendingEdit
                        ? 'border-amber-400/60 bg-amber-500/5'
                        : ''
                  }`}
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground shrink-0 cursor-grab" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className={`font-medium text-sm truncate ${
                          pending ? 'line-through' : ''
                        }`}
                      >
                        {perf.performance_name}
                      </p>
                      {perf.is_slack && (
                        <Badge variant="outline" className="text-xs">
                          <Sun className="h-3 w-3 mr-0.5" /> Slack
                        </Badge>
                      )}
                      {pending && (
                        <Badge
                          variant="outline"
                          className="text-xs text-destructive border-destructive/60"
                        >
                          Pending delete
                        </Badge>
                      )}
                      {pendingEdit && (
                        <Badge
                          variant="outline"
                          className="text-xs text-amber-600 border-amber-400/70"
                        >
                          Unsaved changes
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(perf.performance_date) || 'No date'}
                      {perf.start_time ? ` · ${formatTime(perf.start_time)}` : ''}
                      {perf.default_max_contestants
                        ? ` · Max ${perf.default_max_contestants}`
                        : ''}
                      {pending ? ' · Will be removed on Save Draft' : ''}
                      {pendingEdit ? ' · Will be saved on Save Draft' : ''}
                    </p>
                    {(() => {
                      // Event chips: events whose assignments include this
                      // perf (skipping pending-delete events).
                      const perfEvents = events.filter(
                        (ec) =>
                          !ec._pendingDelete &&
                          ec.performance_assignments.some(
                            (a) => a.performance_key === perf._key,
                          ),
                      );
                      if (perfEvents.length === 0) return null;
                      return (
                        <div className="flex flex-wrap items-center gap-1 mt-1.5">
                          {perfEvents.map((ec) => (
                            <Badge
                              key={ec._key}
                              variant="outline"
                              className="text-[10px] font-normal border-blue-400/50 text-blue-700 dark:text-blue-300 bg-blue-500/10"
                            >
                              {ec.event_type || 'Unnamed event'}
                            </Badge>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  {!pending && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => handleEditClick(perf._key)}
                      aria-label="Edit performance"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-7 w-7 shrink-0 ${
                      pending ? '' : 'text-destructive hover:text-destructive'
                    }`}
                    onClick={() => removePerformance(perf._key)}
                    aria-label={pending ? 'Undo delete' : 'Delete performance'}
                    title={pending ? 'Undo delete' : 'Delete performance'}
                  >
                    {pending ? (
                      <Undo2 className="h-3.5 w-3.5" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              );
            })}
        </div>
      )}

      <PerformanceDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingKey(null);
        }}
        mode={editingKey ? 'edit' : 'add'}
        initialValues={
          editingPerformance
            ? {
                performance_name: editingPerformance.performance_name,
                performance_date: editingPerformance.performance_date,
                start_time: editingPerformance.start_time,
                end_time: editingPerformance.end_time,
                is_slack: editingPerformance.is_slack,
                default_max_contestants: editingPerformance.default_max_contestants,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onSubmitAndNextDay={handleSubmitAndNextDay}
        // Only show the toggle list when editing an existing performance —
        // a brand-new perf has no stable draft identity yet, and events are
        // created in Step 2 (EventConfigStep) before they can be assigned.
        eventsSlot={
          editingKey ? (
            <EventTogglesSlot performanceKey={editingKey} />
          ) : undefined
        }
        timezoneAbbreviation={tzAbbr}
      />
    </div>
  );
}
